import React, { useMemo } from 'react';
import { useData } from '../contexts/DataContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { StatusBadge, CircuitBadge } from '../components/dashboard/StatusBadge';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Server, RefreshCw, Activity, Clock, CheckCircle2, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

// Chart style constants
const CHART_STYLES = {
  tooltip: {
    backgroundColor: 'hsl(var(--card))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
  },
  xAxisTick: { fill: 'hsl(var(--muted-foreground))' },
  yAxisTick: { fill: 'hsl(var(--muted-foreground))' },
};

const Providers = () => {
  const { data, refreshData, isRefreshing } = useData();
  const { providers, rateRequests } = data;

  const handleRefresh = async () => {
    await refreshData();
    toast.success('Provider data refreshed');
  };

  const handleResetCircuit = (providerId) => {
    toast.success(`Circuit breaker reset for ${providerId}`);
  };

  // Prepare latency chart data
  const latencyData = useMemo(() => 
    rateRequests.slice(-12).map((item, index) => ({
      time: `${item.hour}:00`,
      DHL: Math.floor(Math.random() * 500) + 1000,
      Turn14: Math.floor(Math.random() * 400) + 700,
      Zone: Math.floor(Math.random() * 50) + 30,
    })), [rateRequests]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Providers</h1>
          <p className="text-muted-foreground mt-1">
            Monitor and manage shipping rate providers
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Provider Cards */}
      <div className="grid gap-6">
        {providers.map((provider) => (
          <Card key={provider.id} className="card-hover">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Server className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle>{provider.name}</CardTitle>
                    <CardDescription className="mt-1">
                      Provider ID: <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{provider.id}</code>
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={provider.status} />
                  <CircuitBadge state={provider.circuit} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Metrics Grid */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Activity className="h-4 w-4" />
                    <span>Requests Today</span>
                  </div>
                  <div className="text-2xl font-bold">{provider.requestsToday.toLocaleString()}</div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Success Rate</span>
                  </div>
                  <div className="text-2xl font-bold">{provider.successRate}%</div>
                  <Progress value={provider.successRate} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Avg Latency</span>
                  </div>
                  <div className="text-2xl font-bold">{provider.avgLatency}ms</div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <TrendingUp className="h-4 w-4" />
                    <span>Status</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {provider.circuit === 'open' ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleResetCircuit(provider.id)}
                      >
                        Reset Circuit
                      </Button>
                    ) : (
                      <Badge variant="outline" className="bg-success-light text-success border-success/20">
                        Operational
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Provider Details */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 border border-border rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm">Configuration</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Circuit Breaker</span>
                      <span className="font-medium capitalize">{provider.circuit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Timeout</span>
                      <span className="font-medium">5000ms</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Retry Attempts</span>
                      <span className="font-medium">3</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Cache TTL</span>
                      <span className="font-medium">15min</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 border border-border rounded-lg space-y-2">
                  <h4 className="font-semibold text-sm">Recent Activity</h4>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Success</span>
                      <span className="font-medium">2 min ago</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Last Error</span>
                      <span className="font-medium">{provider.lastError || 'None'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Error Count (1h)</span>
                      <span className="font-medium">0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Uptime</span>
                      <span className="font-medium">99.9%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Latency Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Latency Comparison</CardTitle>
          <CardDescription>Average response time over the last 12 hours</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={latencyData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis 
                dataKey="time" 
                tick={CHART_STYLES.xAxisTick}
              />
              <YAxis 
                label={{ value: 'Latency (ms)', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
                tick={CHART_STYLES.yAxisTick}
              />
              <Tooltip contentStyle={CHART_STYLES.tooltip} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="DHL" 
                stroke="hsl(var(--chart-1))" 
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--chart-1))', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="Turn14" 
                stroke="hsl(var(--chart-2))" 
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--chart-2))', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="Zone" 
                stroke="hsl(var(--chart-3))" 
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--chart-3))', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default Providers;
